import React from 'react';
import { OnboardingStep } from '../../../contexts/OnboardingContext';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface FeatureStepProps {
  step: OnboardingStep;
}

export function FeatureStep({ step }: FeatureStepProps) {
  // Mock feature image based on step
  const getFeatureImage = () => {
    if (step.id.includes('dashboard')) {
      return (
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/20 rounded p-4">
              <div className="text-2xl font-bold">12</div>
              <div className="text-sm opacity-80">Active Connections</div>
            </div>
            <div className="bg-white/20 rounded p-4">
              <div className="text-2xl font-bold">2,500+</div>
              <div className="text-sm opacity-80">Available Apps</div>
            </div>
          </div>
        </div>
      );
    } else if (step.id.includes('planning')) {
      return (
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
              App 1
            </div>
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
              App 2
            </div>
          </div>
        </div>
      );
    } else if (step.id.includes('config')) {
      return (
        <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm">
          <div>{"{"}</div>
          <div className="pl-4">"mcpServers": {"{"}</div>
          <div className="pl-8">"gmail": {"{"}</div>
          <div className="pl-12">"command": "npx",</div>
          <div className="pl-12">"args": ["@pipedream/mcp"]</div>
          <div className="pl-8">{"}"}</div>
          <div className="pl-4">{"}"}</div>
          <div>{"}"}</div>
        </div>
      );
    }
    
    return (
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-lg p-12 flex items-center justify-center">
        <InformationCircleIcon className="h-16 w-16 text-gray-400" />
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
        {step.title}
      </h3>
      
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        {step.description}
      </p>

      <div className="mb-6">
        {getFeatureImage()}
      </div>

      {/* Additional tips */}
      {step.id.includes('dashboard') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
            💡 Pro Tip
          </h4>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Use the search bar to quickly find apps you use every day. Popular apps like Gmail, Slack, and GitHub are just a click away!
          </p>
        </div>
      )}

      {step.id.includes('planning') && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">
            🎯 Did You Know?
          </h4>
          <p className="text-sm text-purple-800 dark:text-purple-200">
            You can drag and drop apps to visually design your integration architecture. Connect apps with arrows to show data flow!
          </p>
        </div>
      )}

      {step.id.includes('config') && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
            🚀 Quick Start
          </h4>
          <p className="text-sm text-green-800 dark:text-green-200">
            Your MCP configurations work with Claude Desktop, Cursor, and any MCP-compatible client. Just copy and paste!
          </p>
        </div>
      )}
    </div>
  );
}